import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { useI18n } from "@/i18n";
import { BACKEND } from "@/lib/api";
import { ThemeToggle, useTheme } from "@/context/theme";

const Code = ({ children }) => <pre className="text-xs font-mono bg-secondary rounded-xl p-4 overflow-x-auto whitespace-pre-wrap break-all">{children}</pre>;
const Section = ({ id, title, children }) => (
  <section id={id} className="space-y-3 scroll-mt-24"><h2 className="font-heading text-base md:text-lg font-bold">{title}</h2>{children}</section>
);

export default function Docs() {
  const { t, lang, setLang } = useI18n();
  const { theme } = useTheme();
  const es = lang === "es";
  const ids = ["quick", "api", "button", "webhooks", "plugins", "states"];
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 bg-card/90 backdrop-blur-xl border-b border-border flex items-center justify-between px-5 sm:px-10 h-16">
        <Link to="/"><img src={theme === "dark" ? "/logos/rapidpay-white.png" : "/logos/rapidpay-color.webp"} alt="RapidPay" className="h-7" /></Link>
        <div className="flex items-center gap-1"><ThemeToggle /><button data-testid="docs-language-toggle" onClick={() => setLang(es ? "en" : "es")} className="text-sm font-semibold px-4 min-h-[44px] rounded-full hover:bg-secondary">{es ? "EN" : "ES"}</button></div>
      </header>
      <div className="max-w-6xl mx-auto px-5 sm:px-10 py-10 grid lg:grid-cols-[200px_1fr] gap-10">
        <nav className="hidden lg:block sticky top-24 self-start space-y-1 text-sm">
          {ids.map((i) => <a key={i} href={`#${i}`} className="block px-3 py-2 rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground">{t(`docs.${i}`)}</a>)}
        </nav>
        <div className="space-y-12 max-w-3xl" data-testid="docs-page">
          <div><h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">{t("docs.title")}</h1><p className="mt-4 text-base md:text-lg text-muted-foreground">{t("docs.intro")}</p></div>

          <Section id="quick" title={t("docs.quick")}>
            <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
              <li>{es ? "Crea un link de pago en /cuenta → Links de pago." : "Create a payment link in /cuenta → Payment links."}</li>
              <li>{es ? "Compártelo (URL o QR) o incrústalo con el botón." : "Share it (URL or QR) or embed it with the button."}</li>
              <li>{es ? "Recibe webhooks order.paid y consulta el saldo neto en Saldo." : "Receive order.paid webhooks and check your net balance in Balance."}</li>
            </ol>
            <Code>{`${BACKEND}/pagar/{slug}/{link_id}`}</Code>
          </Section>

          <Section id="api" title={t("docs.api")}>
            <p className="text-sm text-muted-foreground">{t("docs.auth")}</p>
            <Code>{`POST ${BACKEND}/api/v2/tenant/orders
X-Api-Key: rp_live_...
Idempotency-Key: pedido-1001
Content-Type: application/json

{"amount": 25.00, "description": "Pedido #1001", "customer_email": "cliente@correo.com",
 "success_url": "https://tu-sitio.com/gracias", "cancel_url": "https://tu-sitio.com/carrito"}

→ 201 {"id": "...", "order_number": "RPCAF000012", "status": "created", "checkout_url": "...", "qr": "data:image/png;base64,..."}`}</Code>
            <Code>{`GET  /api/v2/tenant/orders?status=paid&q=1001
GET  /api/v2/tenant/orders/{id}
POST /api/v2/tenant/orders/{id}/void
POST /api/v2/tenant/orders/{id}/refund      {"amount": 25.00}
GET  /api/v2/tenant/links · POST /api/v2/tenant/links  {"title": "...", "amount": 10}
GET  /api/v2/tenant/links/{link_id}/qr.png | qr.svg
GET  /api/v2/tenant/balance · GET /api/v2/tenant/ledger`}</Code>
            <p className="text-xs text-muted-foreground">{es ? "Errores: {detail:{code, message}} traducidos según Accept-Language (es/en)." : "Errors: {detail:{code, message}} translated by Accept-Language (es/en)."}</p>
          </Section>

          <Section id="button" title={t("docs.button")}>
            <Code>{`<script src="${BACKEND}/api/v2/public/button.js" data-base="${BACKEND}" defer></script>
<button data-rapidpay-button data-slug="tu-comercio" data-link="Lxxxxxxxxx" data-mode="modal"></button>

<style>:root{--rp-primary:#0fe814;--rp-on-primary:#0b0f19;--rp-radius:12px;--rp-font:inherit}</style>
<script>document.addEventListener('rapidpay:paid', e => console.log(e.detail.order));</script>`}</Code>
          </Section>

          <Section id="webhooks" title={t("docs.webhooks")}>
            <p className="text-sm text-muted-foreground">{t("hooks.sig")}</p>
            <Code>{`POST https://tu-servidor.com/webhook
X-RapidPay-Event: order.paid
X-RapidPay-Timestamp: 1760000000
X-RapidPay-Signature: v1=<hex hmac_sha256(secret, timestamp + "." + body)>

{"id":"evt_...","event":"order.paid","created_at":"...","data":{"order_number":"RPCAF000012","status":"paid","amount":25.0,...}}

# ${es ? "Eventos" : "Events"}: order.pending, order.paid, order.rejected, order.expired, order.voided, order.refunded`}</Code>
          </Section>

          <Section id="plugins" title={t("docs.plugins")}>
            <div className="grid sm:grid-cols-2 gap-3">
              {[["woocommerce", t("docs.woo")], ["wordpress", t("docs.wp")]].map(([k, label]) => (
                <a key={k} href={`${BACKEND}/api/v2/public/plugins/${k}.zip`} data-testid={`docs-download-${k}`} className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 hover:border-primary transition-colors">
                  <span className="font-semibold text-sm">{label}</span><Download className="w-5 h-5 text-muted-foreground" />
                </a>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{es ? "WooCommerce: instala el ZIP, ve a WooCommerce → Ajustes → Pagos → RapidPay, pega tu llave API y slug. WordPress: usa el shortcode [rapidpay link=\"Lxxxx\" slug=\"tu-comercio\"]." : "WooCommerce: install the ZIP, go to WooCommerce → Settings → Payments → RapidPay, paste your API key and slug. WordPress: use the shortcode [rapidpay link=\"Lxxxx\" slug=\"your-store\"]."}</p>
          </Section>

          <Section id="states" title={t("docs.states")}>
            <Code>{`created → pending → paid → refunded
                 ↘ rejected   ↘ voided
created|pending → expired (15 min) | voided`}</Code>
          </Section>
        </div>
      </div>
    </div>
  );
}
